import { useEffect, useState } from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";

import CloseIcon from "@mui/icons-material/Close";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";

import font from "src/styles/font";
import {
  Black,
  DarkGrey,
  Grey,
  Primary,
  White,
  White01,
} from "src/styles/color";
import { checkNotEmpty, convertPrice, formatFromElastic } from "src/common";
import { useLoading } from "src/context/LoadingContext";
import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

import { addProductGroup } from "src/api/deal";
import { searchProduct } from "src/api/product";

const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.md};
  font-weight: 500;
  color: ${Black};
`;

const WrapperTextfield = styled.div`
  width: 100%;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
    // text-align: center;
    // background: #fff;
  }
  .TextField-without-border-radius-amount fieldset {
    border-radius: 16px;
    width: 100%;
    text-align: center;
    background: #fff;
  }
  .MuiAutocomplete-root {
    margin: 0;
  }
  .MuiOutlinedInput-input {
    /* background: #fff; */
    /* border-radius: 16px; */
    padding: 16px;
  }
  .MuiInputBase-root-MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root {
    border-radius: 16px;
  }

  .MuiTextField-root {
    border-radius: 16px;
  }
  .MuiOutlinedInput-root {
    border-radius: 16px;
  }
  .MuiInputAdornment-root {
    font-size: ${font.size.sm};
    color: ${Grey};
  }
  .MuiFormHelperText-root {
    margin-left: 0;
  }
  /* .MuiFormHelperText-root.Mui-error {
    color: ${DarkGrey};
  } */
`;

const LabelTextfield = styled.div`
  font-size: ${font.size.sm};
  font-weight: 400;
  color: ${Black};
`;

const LabelCount = styled.div`
  font-size: ${font.size.sm};
  font-weight: 400;
  color: ${DarkGrey};
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey"
      ? "var(--darkgrey-color)"
      : color === "primary"
        ? "var(--primary-color)"
        : "var(--secondary-color)"};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;
`;

const TextPriceOption = styled(Box)`
  width: 100%;
  color: var(--primary-color);
  font-size: ${font.size.md};
  font-weight: 500;
`;

const ProductBox = styled(Box)`
  width: 100%;
  height: 320px;
  overflow: hidden;
  overflow-y: scroll;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* justify-content: center; */

  ::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #b9b9b9;
    border-radius: 100px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #b9b9b9;
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const EmptyProduct = styled.div`
  font-size: ${font.size.sm};
  font-weight: 400;
  color: ${Grey};
`;

const ListProduct = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
    color: ${Black};
  }
`;

const StyledInputAdornment = styled(InputAdornment)`
  margin: 0;

  &.MuiInputAdornment-root {
    font-size: ${font.size.sm};
    font-weight: 400;
    color: ${Grey};
  }
`;

const AddMultiProductDialog = ({
  initialGroup,
  dealGroups = [],
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();
  const { setLoading } = useLoading();

  const [dataProductList, setDataProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState({
    group: initialGroup ?? null,
    product_choice_deals: [],
  });

  const isFormValid =
    selectedProduct?.group && selectedProduct?.product_choice_deals.length > 0;
  // &&
  // selectedProduct?.product_choice_deals.every((choice) => {
  //   return Object.keys(choice).every((required) => {
  //     let objVal = choice[required];
  //     return checkNotEmpty(objVal) && objVal !== 0;
  //   });
  // });

  const fetchProduct = async () => {
    try {
      setIsLoading(true);
      await searchProduct({ not_in_deal: true }).then(async ({ data }) => {
        // console.log('----- data ----', data)
        setDataProductList(
          data?.rows.length > 0
            ? data?.rows?.map((product) => ({
                ...formatFromElastic(product),
              }))
            : [],
        );
        setIsLoading(false);
      });
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleOnClose = () => {
    openDialog({
      type: "confirm",
      title: `คุณต้องการออกจากหน้านี้ใช่หรือไม่`,
      onConfirm: () => {
        onClose();
      },
      maxWidth: 342,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await addProductGroup({
        _id: selectedProduct.group?._id,
        product_choice_deals: selectedProduct.product_choice_deals.map(
          (choice) => ({
            ...choice,
            product_choice_deal: choice.product_choice_deal?._id,
          }),
        ),
      });

      await enqueueSnackbar("เพิ่มสินค้าสำเร็จ", {
        variant: "success",
      });

      await onSuccess();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, []);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <Dialog
      onClose={handleOnClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1024,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <WrapperTitle>เพิ่มสินค้า</WrapperTitle>

        <IconButton
          aria-label="close"
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Stack sx={{ mb: 0.5 }} flexDirection="row">
            <LabelTextfield>หมวดหมู่</LabelTextfield>
          </Stack>
          <WrapperTextfield>
            <Autocomplete
              // freeSolo
              aria-required={true}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              options={dealGroups}
              value={selectedProduct.group}
              onChange={(e, newValue) => {
                setSelectedProduct({
                  ...selectedProduct,
                  group: newValue,
                });
              }}
              getOptionLabel={(option) => {
                return option?.name;
              }}
              filterOptions={filterOptions}
              sx={{
                width: "100%",
              }}
              disabled={!checkNotEmpty(dealGroups) && !!initialGroup}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    maxWidth: "100%",
                    width: "100%",
                  }}
                  required
                  placeholder="โปรดหมวดหมู่"
                  error={!selectedProduct?.group?._id ? true : false}
                  helperText={"เลือกได้แค่ 1 หมวดหมู่เท่านั้น"}
                />
              )}
            />
          </WrapperTextfield>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Stack
            sx={{ mb: 0.5 }}
            flexDirection="row"
            justifyContent="space-between"
          >
            <LabelTextfield>สินค้า</LabelTextfield>

            <LabelCount>{`${selectedProduct.product_choice_deals.length} สินค้า`}</LabelCount>
          </Stack>
          <WrapperTextfield>
            <Autocomplete
              // freeSolo
              aria-required={true}
              id="search-product-select"
              multiple
              isOptionEqualToValue={(option, value) =>
                option?._id === value?.product_choice_deal?._id
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  // style={{
                  //   borderBottom: "4px solid var(--darkwhite-color)",
                  // }}
                  key={`product-quotation-${option._id}`}
                >
                  <Box
                    sx={{
                      width: "100%",
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                      gap: 2,
                    }}
                  >
                    <Box>
                      {!!option?.group_image?.product_display_image ===
                      false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                            marginRight: "8px",
                          }}
                          src={
                            option?.group_image?.product_display_image
                              ? option?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={option?.group_image?.product_display_image}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        width: "15%",
                        maxWidth: "150px",
                      }}
                    >
                      <TextOption>{option?.product?.item_name}</TextOption>

                      <TextOption color="grey" sx={{ mb: 0 }}>
                        {option?.product?.item_code}
                      </TextOption>
                    </Box>

                    <Box
                      sx={{
                        width: "30%",
                        maxWidth: "300px",
                        textAlign: "center",
                      }}
                    >
                      <TextOption color="grey">
                        {option?.product?.user?.seller?.company_name}
                      </TextOption>
                    </Box>

                    <Box
                      sx={{
                        width: "30%",
                        maxWidth: "300px",
                      }}
                    >
                      <TextOption color="grey">
                        {option?.size?.quantity === "size"
                          ? `${option?.size?.width} ${
                              getLocale() === "th"
                                ? option?.size?.unit_width?.unit_th
                                : option?.size?.unit_width?.unit_en
                            } x ${option?.size?.height} ${
                              getLocale() === "th"
                                ? option?.size?.unit_height?.unit_th
                                : option?.size?.unit_height?.unit_en
                            } x ${option?.size?.thickness} ${
                              getLocale() === "th"
                                ? option?.size?.unit_thickness?.unit_th
                                : option?.size?.unit_thickness?.unit_en
                            }`
                          : option?.size?.quantity === "capacity"
                            ? `${option?.size?.capacity_size} ${
                                getLocale() === "th"
                                  ? option?.size?.unit_capacity?.unit_th
                                  : option?.size?.unit_capacity?.unit_en
                              }`
                            : getLocale() === "th"
                              ? "สินค้าสั่งทำพิเศษ"
                              : "Made by order"}
                      </TextOption>

                      <TextOption color="grey" sx={{ mb: 0 }}>
                        (
                        {getLocale() === "th"
                          ? option?.color?.color_th
                          : option?.color?.color_en}
                        )
                      </TextOption>
                    </Box>

                    <Box
                      sx={{
                        width: "10%",
                        maxWidth: "100px",
                      }}
                    >
                      <TextPriceOption>
                        {`${convertPrice(
                          option?.group_price?.is_start_price === true
                            ? option?.group_price?.start_price
                            : option?.group_price.special_price > 0 &&
                                option?.group_price.special_price !== null &&
                                option?.group_price.special_price !== undefined
                              ? option?.group_price.special_price
                              : option?.group_price?.price,
                        )}`}
                      </TextPriceOption>
                    </Box>
                  </Box>
                </li>
              )}
              options={dataProductList}
              value={selectedProduct.product_choice_deals}
              onChange={(e, newValue, reason) => {
                if (reason !== "removeOption") {
                  setSelectedProduct({
                    ...selectedProduct,
                    product_choice_deals: newValue.map((item) => {
                      if (!!item?.product_choice_deal) return item;

                      return {
                        product_choice_deal: item,
                        price: item?.group_price?.price,
                        special_price: null,
                      };
                    }),
                  });
                }
              }}
              loading={isLoading}
              getOptionLabel={(option) => {
                return (
                  option?.product?.item_name +
                  " - " +
                  option?.product?.item_code +
                  (option?.name !== "" ? ` - (` + option?.name + `)` : ``) +
                  ` [${option?.product?.user?.seller?.company_name}]`
                );
              }}
              renderTags={(value) => null}
              filterSelectedOptions
              filterOptions={filterOptions}
              sx={{
                width: "100%",
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    maxWidth: "100%",
                    width: "100%",
                  }}
                  required
                  placeholder="ค้นหาได้จากชื่อ / รหัส / ร้านค้า"
                />
              )}
            />
          </WrapperTextfield>
        </Box>

        <ProductBox
          sx={{
            ...(selectedProduct.product_choice_deals.length > 0
              ? {
                  backgroundColor: White,
                  justifyContent: "flex-start",
                }
              : {
                  backgroundColor: White01,
                  justifyContent: "center",
                }),
          }}
        >
          {selectedProduct.product_choice_deals.length > 0 ? (
            <>
              {selectedProduct.product_choice_deals.map(
                (
                  { product_choice_deal: product, price, special_price },
                  index,
                ) => (
                  <ListProduct key={`selected-product-${index}`}>
                    <span>{index + 1}.</span>

                    <Box sx={{ width: 80 }}>
                      {!!product?.group_image?.product_display_image ===
                      false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                            marginRight: "8px",
                          }}
                          src={
                            product?.group_image?.product_display_image
                              ? product?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={product?.group_image?.product_display_image}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        width: "18%",
                        maxWidth: "200px",
                      }}
                    >
                      <TextOption>{product?.product?.item_name}</TextOption>

                      <TextOption color="grey" sx={{ mb: 0 }}>
                        {product?.product?.item_code}
                      </TextOption>
                    </Box>

                    <Box
                      sx={{
                        width: "17%",
                        maxWidth: "200px",
                        textAlign: "center",
                      }}
                    >
                      <TextOption color="grey">
                        {product?.product?.user?.seller?.company_name}
                      </TextOption>
                    </Box>

                    <Box
                      sx={{
                        width: "18%",
                        maxWidth: "200px",
                      }}
                    >
                      <TextOption color="grey">
                        {product?.size?.quantity === "size"
                          ? `${product?.size?.width} ${
                              getLocale() === "th"
                                ? product?.size?.unit_width?.unit_th
                                : product?.size?.unit_width?.unit_en
                            } x ${product?.size?.height} ${
                              getLocale() === "th"
                                ? product?.size?.unit_height?.unit_th
                                : product?.size?.unit_height?.unit_en
                            } x ${product?.size?.thickness} ${
                              getLocale() === "th"
                                ? product?.size?.unit_thickness?.unit_th
                                : product?.size?.unit_thickness?.unit_en
                            }`
                          : product?.size?.quantity === "capacity"
                            ? `${product?.size?.capacity_size} ${
                                getLocale() === "th"
                                  ? product?.size?.unit_capacity?.unit_th
                                  : product?.size?.unit_capacity?.unit_en
                              }`
                            : getLocale() === "th"
                              ? "สินค้าสั่งทำพิเศษ"
                              : "Made by order"}
                      </TextOption>

                      <TextOption color="grey" sx={{ mb: 0 }}>
                        (
                        {getLocale() === "th"
                          ? product?.color?.color_th
                          : product?.color?.color_en}
                        )
                      </TextOption>
                    </Box>

                    <Box
                      sx={{
                        width: "30%",
                        maxWidth: "250px",
                      }}
                    >
                      <Stack gap={1}>
                        <Box>
                          <TextField
                            variant="outlined"
                            fullWidth
                            required
                            value={price}
                            sx={{
                              ".MuiOutlinedInput-input": {
                                px: 2,
                                py: 1,
                                textAlign: "right",
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <StyledInputAdornment position="start">
                                  <EditIcon
                                    fontSize="10"
                                    sx={{ color: Grey, mr: 1 }}
                                  />
                                  ราคาปกติ
                                </StyledInputAdornment>
                              ),
                            }}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0) {
                                let dataChoice =
                                  selectedProduct.product_choice_deals;
                                dataChoice[index].price = e.target.value;
                                setSelectedProduct({
                                  ...selectedProduct,
                                  product_choice_deals: [...dataChoice],
                                });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0) {
                                let dataChoice =
                                  selectedProduct.product_choice_deals;
                                dataChoice[index].price =
                                  parseFloat(e.target.value) || 0;
                                setSelectedProduct({
                                  ...selectedProduct,
                                  product_choice_deals: [...dataChoice],
                                });
                              }
                            }}
                          />
                        </Box>

                        <Box>
                          <TextField
                            variant="outlined"
                            fullWidth
                            required
                            value={special_price}
                            sx={{
                              ".MuiOutlinedInput-input": {
                                px: 2,
                                py: 1,
                                textAlign: "right",
                                color: Primary,
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <StyledInputAdornment position="start">
                                  <EditIcon
                                    fontSize="10"
                                    sx={{ color: Grey, mr: 1 }}
                                  />
                                  ราคาลด
                                </StyledInputAdornment>
                              ),
                            }}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0) {
                                let dataChoice =
                                  selectedProduct.product_choice_deals;
                                dataChoice[index].special_price =
                                  e.target.value;
                                setSelectedProduct({
                                  ...selectedProduct,
                                  product_choice_deals: [...dataChoice],
                                });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0) {
                                let dataChoice =
                                  selectedProduct.product_choice_deals;
                                dataChoice[index].special_price =
                                  parseFloat(e.target.value) || null;
                                setSelectedProduct({
                                  ...selectedProduct,
                                  product_choice_deals: [...dataChoice],
                                });
                              }
                            }}
                            // error={special_price === 0}
                          />
                        </Box>
                      </Stack>
                    </Box>

                    <Box>
                      <IconButton
                        aria-label="delete"
                        onClick={() => {
                          let data = selectedProduct.product_choice_deals;
                          data.splice(index, 1);
                          setSelectedProduct({
                            ...selectedProduct,
                            product_choice_deals: [...data],
                          });
                        }}
                        sx={{
                          width: "30px",
                          p: "6px",
                          backgroundColor: "var(--darkwhite-color)",
                          ":disabled": {
                            ".MuiSvgIcon-root": {
                              color: "var(--darkgrey-color)",
                            },
                          },
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: "var(--black-color)",
                            width: "16px",
                            height: "18px",
                          }}
                        />
                      </IconButton>
                    </Box>
                  </ListProduct>
                ),
              )}
            </>
          ) : (
            <EmptyProduct>ไม่มีสินค้า</EmptyProduct>
          )}
        </ProductBox>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={handleOnClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการเพิ่มสินค้าใช่หรือไม่`,
              onConfirm: () => {
                handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
          // autoFocus // enable auto focus to increase accessibility
          disabled={!isFormValid}
        >
          เพิ่ม
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMultiProductDialog;
